// Parametrický codec systém pro navigační jádro

/** Kontrakt pro serializaci/deserializaci jednoho parametru do/z URL stringu */
export interface ParamCodec<T> {
  encode(value: T): string;
  decode(raw: string): T;
}

/** Definice jednoho parametru v codec mapě */
export interface ParamDefinition<T = unknown> {
  readonly codec: ParamCodec<T>;
  readonly required: boolean;
  readonly defaultValue?: T;
}

/** Codec mapa – klíč -> definice parametru */
export type ParamCodecMap = Record<string, ParamDefinition>;

/** Helper typ pro extrakci vstupního tvaru z codec mapy */
export type ParamInput<M extends ParamCodecMap> = {
  [K in keyof M as M[K]['required'] extends true ? K : never]: M[K] extends ParamDefinition<infer T>
    ? T
    : never;
} & {
  [K in keyof M as M[K]['required'] extends true ? never : K]?: M[K] extends ParamDefinition<
    infer T
  >
    ? T
    : never;
};

/** Helper typ pro extrakci výstupního tvaru z codec mapy */
export type ParamOutput<M extends ParamCodecMap> = {
  [K in keyof M as M[K]['required'] extends true ? K : never]: M[K] extends ParamDefinition<infer T>
    ? T
    : never;
} & {
  [K in keyof M as M[K]['required'] extends true ? never : K]?: M[K] extends ParamDefinition<
    infer T
  >
    ? T | undefined
    : never;
};

/** Builder pro definici jednoho parametru */
interface ParamBuilder<T> {
  readonly codec: ParamCodec<T>;
  readonly required: true;
  optional(): ParamDefinition<T> & { readonly required: false };
}

function createParamDefinition<T>(codec: ParamCodec<T>): ParamDefinition<T> & ParamBuilder<T> {
  return {
    codec,
    required: true as const,
    optional() {
      return { codec, required: false as const };
    },
  };
}

/** Vestavěné codec factory */
export const param = {
  string(): ParamDefinition<string> & ParamBuilder<string> {
    return createParamDefinition<string>({
      encode: (v) => v,
      decode: (r) => r,
    });
  },

  number(): ParamDefinition<number> & ParamBuilder<number> {
    return createParamDefinition<number>({
      encode: (v) => String(v),
      decode: (r) => {
        const n = Number(r);
        if (Number.isNaN(n)) throw new Error(`Invalid number: ${r}`);
        return n;
      },
    });
  },

  boolean(): ParamDefinition<boolean> & ParamBuilder<boolean> {
    return createParamDefinition<boolean>({
      encode: (v) => (v ? '1' : '0'),
      decode: (r) => r === '1' || r === 'true',
    });
  },

  enum<const T extends readonly string[]>(
    values: T
  ): ParamDefinition<T[number]> & ParamBuilder<T[number]> {
    return createParamDefinition<T[number]>({
      encode: (v) => v,
      decode: (r) => {
        if (!values.includes(r)) {
          throw new Error(`Invalid enum value: "${r}". Expected one of: ${values.join(', ')}`);
        }
        return r as T[number];
      },
    });
  },

  uuid(): ParamDefinition<string> & ParamBuilder<string> {
    return createParamDefinition<string>({
      encode: (v) => v,
      decode: (r) => {
        if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(r)) {
          throw new Error(`Invalid UUID: ${r}`);
        }
        return r;
      },
    });
  },

  custom<T>(codec: ParamCodec<T>): ParamDefinition<T> & ParamBuilder<T> {
    return createParamDefinition(codec);
  },
};
